/**
 * Cron feature (Preact): lists the workspace's scheduled jobs and lets the user
 * create, edit, enable/disable, and delete them. Each job runs a prompt in a
 * target session on a cron schedule or at a specific time.
 */
import { useState, useEffect, useCallback } from 'preact/hooks';
import { listCron, createCron, updateCron, deleteCron, setCronEnabled, runCron } from '../lib/api.js';
import { Icon } from '../components/Icons.jsx';

const fmtTime = (t) => (t ? new Date(t).toLocaleString() : '—');

// toLocalInput converts an RFC3339 timestamp to a value usable by
// <input type="datetime-local"> (yyyy-MM-ddTHH:mm).
const toLocalInput = (t) => {
  if (!t) return '';
  const d = new Date(t);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// fromLocalInput converts a datetime-local value to RFC3339 string.
const fromLocalInput = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return d.toISOString();
};

// Day-of-week labels for the weekly frequency (0 = Sunday).
const DOW_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

// isCustomFreq reports whether a frequency choice exposes a raw cron field.
const isCustomFreq = (freq) => freq === 'custom';

// timeToCronParts converts "HH:MM" to [minute, hour] strings.
const timeToCronParts = (t) => {
  if (!t) return null;
  const [h, m] = t.split(':');
  return [String(Number(m) || 0), String(Number(h) || 0)];
};

// buildSchedule composes the schedule string for a recurring job. When a
// frequency preset that supports it has a time set, a 5-field cron expression
// is produced (e.g. "0 4 * * *" for daily at 04:00); otherwise the descriptor
// form is used (e.g. "@daily"). Custom uses the raw expression.
// Returns { schedule, display } where display is a human label.
function buildSchedule(freq, time, dow, dom) {
  if (freq === 'custom') return null;
  if (freq === 'hourly') return { schedule: '@hourly', display: 'Every hour' };

  const parts = timeToCronParts(time);
  if (!parts) {
    const desc = { daily: '@daily', weekly: '@weekly', monthly: '@monthly' }[freq];
    return { schedule: desc, display: friendlySchedule(desc) };
  }
  const [minute, hour] = parts;
  let expr;
  let display;
  if (freq === 'daily') {
    expr = `${minute} ${hour} * * *`;
    display = `Every day at ${formatTime(hour, minute)}`;
  } else if (freq === 'weekly') {
    expr = `${minute} ${hour} * * ${dow}`;
    display = `${DOW_LABELS[dow]} at ${formatTime(hour, minute)}`;
  } else {
    expr = `${minute} ${hour} ${dom} * *`;
    display = `Monthly on day ${dom} at ${formatTime(hour, minute)}`;
  }
  return { schedule: expr, display };
}

// parseScheduleToForm reverses a stored schedule into form state. It returns
// { freq, time, dow, dom, schedule } where schedule is the raw custom text.
function parseScheduleToForm(s) {
  const base = { freq: 'daily', time: '', dow: 1, dom: 1, schedule: s || '' };
  if (!s) return base;
  if (s === '@hourly') return { ...base, freq: 'hourly' };
  if (s === '@daily') return { ...base, freq: 'daily' };
  if (s === '@weekly') return { ...base, freq: 'weekly' };
  if (s === '@monthly') return { ...base, freq: 'monthly' };

  // Standard 5-field cron: minute hour dom month dow
  const m = s.match(/^(\d+)\s+(\d+)\s+(\*|\d+)\s+\*\s+(\*|\d+)$/);
  if (m) {
    const time = `${String(Number(m[2])).padStart(2, '0')}:${String(Number(m[1])).padStart(2, '0')}`;
    if (m[4] === '*') return { ...base, freq: 'daily', time };
    if (m[3] === '*') return { ...base, freq: 'weekly', time, dow: Number(m[4]) };
    return { ...base, freq: 'monthly', time, dom: Number(m[3]) };
  }

  return { ...base, freq: 'custom' };
}

// formatTime renders hour/minute as "4:00 AM".
const formatTime = (h, m) => {
  const hour = Number(h);
  const period = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${String(Number(m)).padStart(2, '0')} ${period}`;
};

// friendlySchedule renders a schedule descriptor or common cron expression as a
// human label.
const friendlySchedule = (s) => {
  if (!s) return '';
  const labels = {
    '@hourly': 'Every hour',
    '@daily': 'Every day',
    '@weekly': 'Every week',
    '@monthly': 'Every month',
    '@yearly': 'Every year',
  };
  if (labels[s]) return labels[s];
  const m = s.match(/^(\d+)\s+(\d+)\s+(\*|\d+)\s+\*\s+(\*|\d+)$/);
  if (m) {
    const time = formatTime(m[2], m[1]);
    if (m[4] === '*') return `Every day at ${time}`;
    if (m[3] === '*') return `${DOW_LABELS[Number(m[4])]} at ${time}`;
    return `Monthly on day ${m[3]} at ${time}`;
  }
  return s;
};

export function CronPage({ sessions }) {
  const [jobs, setJobs] = useState(null);
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState(null); // job being edited, or 'new'
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setJobs(null);
    return listCron()
      .then(setJobs)
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sessionOptions = sessions ? Array.from(sessions.values()) : [];

  const onDelete = async (job) => {
    if (!window.confirm(`Delete cron job "${job.prompt.slice(0, 50)}"?`)) return;
    try {
      await deleteCron(job.id);
      load();
    } catch (e) {
      window.alert(`Delete failed: ${e.message}`);
    }
  };

  const onToggle = async (job) => {
    try {
      await setCronEnabled(job.id, !job.enabled);
      load();
    } catch (e) {
      window.alert(`Toggle failed: ${e.message}`);
    }
  };

  const onRun = async (job) => {
    try {
      await runCron(job.id);
      load();
    } catch (e) {
      window.alert(`Run failed: ${e.message}`);
    }
  };

  if (editing) {
    return (
      <CronForm
        job={editing === 'new' ? null : editing}
        sessions={sessionOptions}
        onCancel={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
        onSavingChange={setSaving}
      />
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex items-center gap-4 px-4 py-3 border-b border-base-300 bg-base-100">
        <h1 className="text-lg font-semibold text-base-content truncate">Scheduled</h1>
        <button
          type="button"
          className="btn btn-primary btn-sm ml-auto"
          onClick={() => setEditing('new')}
        >
          <Icon name="plus" className="w-4 h-4" />
          New Job
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="w-full max-w-3xl mx-auto flex flex-col gap-3">
          {error ? (
            <p className="text-base-content/60 text-sm">Failed to load cron jobs.</p>
          ) : jobs === null ? (
            <p className="text-base-content/60 text-sm">Loading…</p>
          ) : jobs.length === 0 ? (
            <p className="text-base-content/50 text-sm text-center py-8">
              No cron jobs yet. Create one to schedule a prompt.
            </p>
          ) : (
            jobs.map((job) => (
              <JobRow
                key={job.id}
                job={job}
                sessions={sessionOptions}
                onEdit={() => setEditing(job)}
                onToggle={() => onToggle(job)}
                onRun={() => onRun(job)}
                onDelete={() => onDelete(job)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function JobRow({ job, sessions, onEdit, onToggle, onRun, onDelete }) {
  const session = sessions.find((s) => s.id === job.session_id);
  const when = job.schedule
    ? `Every ${friendlySchedule(job.schedule)}`
    : `Once at ${fmtTime(job.at)}`;
  return (
    <div className="rounded-xl border border-base-300 bg-base-100 p-4">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={`badge badge-sm ${job.enabled ? 'badge-success' : 'badge-ghost'}`}
            >
              {job.enabled ? 'enabled' : 'disabled'}
            </span>
            <span className="text-xs text-base-content/50">{when}</span>
          </div>
          <p className="mt-1 text-sm text-base-content">{job.prompt}</p>
          <p className="mt-1 text-xs text-base-content/50">
            Session: {session ? session.title : job.session_id}
          </p>
          <p className="mt-0.5 text-xs text-base-content/40">
            Last run: {fmtTime(job.last_run_at)} · Next: {fmtTime(job.next_run_at)}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button type="button" className="btn btn-ghost btn-xs" onClick={onToggle}>
            {job.enabled ? 'Disable' : 'Enable'}
          </button>
          <button type="button" className="btn btn-ghost btn-xs" onClick={onRun}>
            Run Now
          </button>
          <button type="button" className="btn btn-ghost btn-xs" onClick={onEdit}>
            Edit
          </button>
          <button type="button" className="btn btn-ghost btn-xs text-error" onClick={onDelete}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
function CronForm({ job, sessions, onCancel, onSaved, onSavingChange }) {
  const [sessionId, setSessionId] = useState(job ? job.session_id : '');
  const [prompt, setPrompt] = useState(job ? job.prompt : '');
  const parsed = parseScheduleToForm(job ? job.schedule : '');
  const [freq, setFreq] = useState(parsed.freq);
  const [timeInput, setTimeInput] = useState(parsed.time);
  const [dow, setDow] = useState(parsed.dow);
  const [dom, setDom] = useState(parsed.dom);
  const [schedule, setSchedule] = useState(parsed.schedule);
  const [at, setAt] = useState(job ? toLocalInput(job.at) : '');
  const [enabled, setEnabled] = useState(job ? job.enabled : true);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    let effectiveSchedule = '';
    if (!at) {
      if (freq === 'custom') {
        effectiveSchedule = schedule;
      } else {
        const built = buildSchedule(freq, timeInput, dow, dom);
        effectiveSchedule = built ? built.schedule : '';
      }
    }
    const payload = {
      session_id: sessionId,
      prompt,
      schedule: effectiveSchedule,
      at: fromLocalInput(at),
      enabled,
    };
    onSavingChange(true);
    try {
      if (job) await updateCron(job.id, payload);
      else await createCron(payload);
      onSaved();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      onSavingChange(false);
    }
  };

  const showTime = !at && !isCustomFreq(freq);
  const showDow = showTime && freq === 'weekly';
  const showDom = showTime && freq === 'monthly';

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex items-center gap-4 px-4 py-3 border-b border-base-300 bg-base-100">
        <h1 className="text-lg font-semibold text-base-content truncate">
          {job ? 'Edit Cron Job' : 'New Cron Job'}
        </h1>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        <form onSubmit={submit} className="w-full max-w-2xl mx-auto flex flex-col gap-4">
          <label className="flex flex-col gap-1">
            <span className="text-sm text-base-content/70">Session</span>
            <select
              className="select select-bordered select-sm w-full"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              required
            >
              <option value="" disabled>
                Select a session…
              </option>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm text-base-content/70">Prompt</span>
            <textarea
              className="textarea textarea-bordered textarea-sm w-full"
              rows={4}
              value={prompt}
              placeholder="e.g. Write a daily status report"
              onChange={(e) => setPrompt(e.target.value)}
              required
            />
          </label>

          <div className="flex flex-col gap-1">
            <span className="text-sm text-base-content/70">Trigger (exactly one)</span>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-sm text-base-content/80">
                <input
                  type="radio"
                  name="trigger"
                  checked={!at}
                  onChange={() => setAt('')}
                />
                Recurring schedule
              </label>
              <div className="flex flex-col gap-2">
                <select
                  className="select select-bordered select-sm w-full disabled:opacity-50"
                  value={freq}
                  disabled={!!at}
                  onChange={(e) => setFreq(e.target.value)}
                >
                  <option value="hourly">Every hour</option>
                  <option value="daily">Every day</option>
                  <option value="weekly">Every week</option>
                  <option value="monthly">Every month</option>
                  <option value="custom">Custom (cron expression / descriptor)</option>
                </select>

                {showTime && (
                  <div className="flex items-center gap-2">
                    <label className="flex-1 flex items-center gap-2 text-sm text-base-content/70">
                      <span>At</span>
                      <input
                        type="time"
                        className="input input-bordered input-sm flex-1"
                        value={timeInput}
                        onChange={(e) => setTimeInput(e.target.value)}
                      />
                    </label>
                    {showDow && (
                      <select
                        className="select select-bordered select-sm"
                        value={dow}
                        onChange={(e) => setDow(Number(e.target.value))}
                      >
                        {DOW_LABELS.map((label, i) => (
                          <option key={label} value={i}>
                            {label}
                          </option>
                        ))}
                      </select>
                    )}
                    {showDom && (
                      <select
                        className="select select-bordered select-sm"
                        value={dom}
                        onChange={(e) => setDom(Number(e.target.value))}
                      >
                        {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                          <option key={d} value={d}>
                            Day {d}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {isCustomFreq(freq) && !at && (
                  <input
                    type="text"
                    className="input input-bordered input-sm w-full"
                    placeholder="e.g. 0 9 * * * or @every 30m or @daily"
                    value={schedule}
                    onChange={(e) => setSchedule(e.target.value)}
                  />
                )}
              </div>
              <label className="flex items-center gap-2 text-sm text-base-content/80">
                <input
                  type="radio"
                  name="trigger"
                  checked={!!at}
                  onChange={() => {
                    setSchedule('');
                    setFreq('daily');
                  }}
                />
                Once at a specific time
              </label>
              <input
                type="datetime-local"
                className="input input-bordered input-sm w-full disabled:opacity-50"
                value={at}
                disabled={!at}
                onChange={(e) => setAt(e.target.value)}
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-base-content/80">
            <input
              type="checkbox"
              className="toggle toggle-sm"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            Enabled
          </label>

          {err && <p className="text-error text-sm">{err}</p>}

          <div className="flex items-center gap-2 pt-2">
            <button type="submit" className="btn btn-primary btn-sm">
              Save
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}